import {
  ArrowsOutIcon,
  BookmarkSimpleIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import Placeholder from "@tiptap/extension-placeholder";
import type { EditorView } from "@tiptap/pm/view";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import IconButton from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import AutoCloseBrackets from "./AutoCloseBracketsExtension";
import BooleanHighlight from "./BooleanHighlightExtension";
import MatchingBracketHighlight from "./MatchingBracketHighlightExtension";
import { useEditorContentSync } from "./useEditorContentSync";

export interface InlineSearchEditorProps {
  handleSearch: (value: string) => void;
  handleExpand: () => void;
  content?: string;
  handleSave?: (editor: Editor) => void;
  isSearching?: boolean;
  onUpdate?: (editor: Editor) => void;
}

export interface InlineSearchEditorRef {
  focus: () => void;
}

const InlineSearchEditor = forwardRef<
  InlineSearchEditorRef,
  InlineSearchEditorProps
>(
  (
    {
      content,
      handleExpand,
      handleSave,
      handleSearch,
      isSearching = false,
      onUpdate,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastInteractionWasKeyboard = useRef(false);
    const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
      const handleTab = (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          lastInteractionWasKeyboard.current = true;
        }
      };

      const handleMouseDown = () => {
        lastInteractionWasKeyboard.current = false;
        setIsKeyboardFocused(false);
      };

      document.addEventListener("keydown", handleTab);
      document.addEventListener("mousedown", handleMouseDown);

      return () => {
        document.removeEventListener("keydown", handleTab);
        document.removeEventListener("mousedown", handleMouseDown);
      };
    }, []);

    const editorRef = useRef<Editor | null>(null);
    const isUpdatingRef = useRef(false);

    const handleSearchRef = useRef(handleSearch);
    handleSearchRef.current = handleSearch;
    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;

    const editorProps = useMemo(
      () => ({
        attributes: {
          class: cn(
            "text-xs font-sans tracking-wide text-gray-800",
            "overflow-auto whitespace-normal [&::-webkit-scrollbar]:hidden scrollbar-none",
            "max-w-[calc(100%-3rem)] no-scrollbar [&>p]:block",
            "outline-hidden!",
            "focus:outline-hidden!",
          ),
        },
        handleKeyDown: (_view: EditorView, event: KeyboardEvent) => {
          if (event.key === "Enter") {
            event.preventDefault(); // Prevents new line creation
            // editorRef is used instead of a direct editor reference because
            // editorProps is memoized with [] — editor from useEditor is not
            // in scope here. The ref is always up-to-date via the sync effect.
            if (editorRef.current) {
              const plainText = editorRef.current.getText().trim();
              handleSearchRef.current(plainText);
            }
            return true; // Handled
          }
          return false; // Let other keys behave normally
        },
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          // Prevent the default paste behavior
          event.preventDefault();

          // Get the clipboard data
          const text = event.clipboardData?.getData("text/plain") || "";

          // Insert the text at the current position with no styling
          const { state, dispatch } = view;
          const { tr } = state;
          tr.insertText(text, tr.selection.from, tr.selection.to);
          dispatch(tr);

          return true; // Indicate that we handled the paste event
        },
      }),
      [],
    );

    const onEditorUpdate = useCallback(({ editor }: { editor: Editor }) => {
      if (isUpdatingRef.current) return;

      if (editor.isEmpty) {
        handleSearchRef.current("");
      }
      onUpdateRef.current?.(editor);
    }, []);

    const onEditorFocus = useCallback(() => {
      setIsEditorFocused(true);
      if (lastInteractionWasKeyboard.current) {
        setIsKeyboardFocused(true);
      }
    }, []);

    const onEditorBlur = useCallback(() => {
      setIsEditorFocused(false);
      setIsKeyboardFocused(false);
    }, []);

    const editor = useEditor({
      extensions: [
        Placeholder.configure({
          placeholder: "Search...",
          emptyEditorClass: cn(
            "cursor-text tracking-wide before:content-[attr(data-placeholder)]",
            "before:absolute before:opacity-50",
            "before-pointer-events-none min-w-1",
          ),
        }),
        StarterKit.configure({
          bold: false,
          italic: false,
          heading: false,
          paragraph: {
            HTMLAttributes: {
              class: "inline m-0 p-0 whitespace-nowrap",
            },
          },
        }),
        BooleanHighlight,
        AutoCloseBrackets,
        MatchingBracketHighlight,
      ],
      content,
      editorProps,
      onUpdate: onEditorUpdate,
      onFocus: onEditorFocus,
      onBlur: onEditorBlur,
    });

    // Sync editor content when content prop changes
    useEditorContentSync(editor, content, isUpdatingRef);

    // Keep editor ref in sync
    useEffect(() => {
      editorRef.current = editor;
    }, [editor]);

    const onSearch = useCallback(() => {
      const ed = editorRef.current;
      if (!ed) return;
      const plainText = ed.getText().trim();
      handleSearchRef.current(plainText);
      setTimeout(() => {
        ed.commands.focus("end");
      }, 0);
    }, []);

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (editor) {
          editor.commands.focus("end");
        }
      },
    }));

    if (!editor) {
      return <p>Loading editor...</p>;
    }

    return (
      <div className="flex w-80 drop-shadow-xs">
        <div className="relative flex-1" ref={containerRef}>
          <div
            className={cn(
              "flex h-[30px] items-center border border-gray-200 border-r-0 rounded-l-md font-normal p-2 bg-white overflow-hidden",
              isEditorFocused && "border-blue-600",
            )}
          >
            {/*
              The focus ring must target the ProseMirror element directly — it
              is the element that receives keyboard focus, not this wrapper div.
              We use a CSS descendant selector via the data attribute so that
              editorProps stays stable (required for the loop fix) while still
              applying the ring only during keyboard navigation.
            */}
            <div
              data-keyboard-focus={isKeyboardFocused ? "true" : undefined}
              className="flex-1 min-w-0 [&[data-keyboard-focus='true']_.ProseMirror]:outline-solid! [&[data-keyboard-focus='true']_.ProseMirror]:outline-2! [&[data-keyboard-focus='true']_.ProseMirror]:outline-blue-750! [&[data-keyboard-focus='true']_.ProseMirror]:outline-offset-2! [&[data-keyboard-focus='true']_.ProseMirror]:rounded-xs!"
            >
              <EditorContent editor={editor} />
            </div>

            <div className="absolute right-0 flex items-center ml-2">
              {!editor.isEmpty && handleSave && (
                <IconButton
                  icon={<BookmarkSimpleIcon />}
                  aria-label={"save key topics"}
                  size={"sm"}
                  variant={"icon-only"}
                  className={"p-0"}
                  tooltipLabel={"Save as key topic"}
                  crossOffset={-60}
                  onPressChange={() => handleSave(editor)}
                />
              )}

              <IconButton
                icon={<ArrowsOutIcon />}
                aria-label="expand"
                size="sm"
                variant="icon-only"
                className="p-1 mr-1"
                tooltipLabel="Expand"
                crossOffset={30}
                onPressChange={handleExpand}
              />
            </div>
          </div>
        </div>
        <IconButton
          icon={<MagnifyingGlassIcon />}
          aria-label="search key topic"
          size="sm"
          className={cn(
            "rounded-none rounded-r-md h-[30px] w-8",
            !isEditorFocused && " border border-gray-200 bg-white",
          )}
          onPress={onSearch}
          variant={!isEditorFocused ? "icon-only" : "default"}
          isPending={isSearching}
        />
      </div>
    );
  },
);

InlineSearchEditor.displayName = "InlineSearchEditor";

export default InlineSearchEditor;
